import { useEffect, useMemo, useRef, useState, type ChangeEvent } from "react";
import { format } from "date-fns";
import { CalendarIcon } from "lucide-react";

import type { Project, TimeEntry } from "@zentrack/domain";
import {
  Banner,
  Button,
  Calendar,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Field,
  FieldGroup,
  FieldLabel,
  Input,
  Label,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Textarea,
} from "@zentrack/ui";

import { fromDateTimeLocalValue, toDateTimeLocalValue } from "../lib/date-helpers";

export interface EntryEditorValues {
  projectId: string;
  note: string;
  startTime: string;
  endTime: string;
}

export interface EntryEditorDialogProps {
  defaultProjectId?: string;
  entry?: TimeEntry | null;
  onOpenChange: (open: boolean) => void;
  onSave: (values: EntryEditorValues) => Promise<void>;
  open: boolean;
  projectLabel: (projectId: string) => string;
  projects: Project[];
}

function parseLocal(value: string): Date | undefined {
  if (!value) return undefined;
  const d = new Date(value);
  return isNaN(d.getTime()) ? undefined : d;
}

function extractTime(value: string): string {
  if (!value) return "00:00";
  const parts = value.split("T");
  return parts[1]?.slice(0, 5) ?? "00:00";
}

function combineDateAndTime(date: Date, time: string): string {
  const [hours, minutes] = time.split(":").map(Number);
  const result = new Date(date);
  result.setHours(hours ?? 0, minutes ?? 0, 0, 0);
  return toDateTimeLocalValue(result.toISOString());
}

interface DateTimePickerProps {
  id?: string;
  value: string;
  onChange: (value: string) => void;
  label: string;
}

function DateTimePicker({ id, value, onChange, label }: DateTimePickerProps) {
  const [open, setOpen] = useState(false);
  const selected = parseLocal(value);
  const timeValue = extractTime(value);

  return (
    <Field>
      <FieldLabel htmlFor={id}>{label}</FieldLabel>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger
          render={
            <Button id={id} variant="outline" className="w-full justify-start gap-2 font-normal">
              <CalendarIcon className="size-4 text-muted-foreground" />
              {selected ? (
                <span>{format(selected, "dd/MM/yyyy")}</span>
              ) : (
                <span className="text-muted-foreground">Pick a date</span>
              )}
              <span className="ml-auto text-muted-foreground">{timeValue}</span>
            </Button>
          }
        />
        <PopoverContent className="w-auto p-0" align="start">
          <Calendar
            mode="single"
            selected={selected}
            onSelect={(date) => {
              if (date) {
                onChange(combineDateAndTime(date, timeValue));
                setOpen(false);
              }
            }}
            autoFocus
          />
          <div className="border-t border-border px-3 py-2">
            <div className="flex items-center gap-2">
              <Label className="w-10 shrink-0 text-xs text-muted-foreground">Time</Label>
              <Input
                type="time"
                value={timeValue}
                onChange={(e: ChangeEvent<HTMLInputElement>) => {
                  const base = selected ?? new Date();
                  onChange(combineDateAndTime(base, e.target.value));
                }}
                className="h-7 text-sm"
              />
            </div>
          </div>
        </PopoverContent>
      </Popover>
    </Field>
  );
}

export function EntryEditorDialog({
  defaultProjectId,
  entry,
  onOpenChange,
  onSave,
  open,
  projectLabel,
  projects,
}: EntryEditorDialogProps) {
  const [projectId, setProjectId] = useState(defaultProjectId ?? "");
  const [note, setNote] = useState("");
  const [startTime, setStartTime] = useState("");
  const [endTime, setEndTime] = useState("");
  const projectsRef = useRef(projects);
  projectsRef.current = projects;

  useEffect(() => {
    if (!open) {
      return;
    }

    if (entry) {
      setProjectId(entry.projectId);
      setNote(entry.note);
      setStartTime(toDateTimeLocalValue(entry.startTime));
      setEndTime(toDateTimeLocalValue(entry.endTime));
      return;
    }

    const baseStart = new Date();
    baseStart.setMinutes(0, 0, 0);
    const baseEnd = new Date(baseStart.getTime() + 60 * 60 * 1000);

    setProjectId(defaultProjectId ?? projectsRef.current[0]?.id ?? "");
    setNote("");
    setStartTime(toDateTimeLocalValue(baseStart.toISOString()));
    setEndTime(toDateTimeLocalValue(baseEnd.toISOString()));
  }, [defaultProjectId, entry, open]);

  const validationMessage = useMemo(() => {
    if (!projectId) {
      return "Choose a project before saving the entry.";
    }

    if (!startTime || !endTime) {
      return "Start and end time are required.";
    }

    if (new Date(endTime) <= new Date(startTime)) {
      return "The session end time must be later than the start time.";
    }

    return null;
  }, [endTime, projectId, startTime]);

  return (
    <Dialog onOpenChange={onOpenChange} open={open}>
      <DialogContent className="w-[min(92vw,760px)]">
        <form
          className="flex flex-col gap-4"
          onSubmit={(event) => {
            event.preventDefault();
            if (validationMessage) {
              return;
            }

            void onSave({
              projectId,
              note: note.trim(),
              startTime: fromDateTimeLocalValue(startTime),
              endTime: fromDateTimeLocalValue(endTime),
            });
          }}
        >
          <DialogHeader>
            <DialogTitle>{entry ? "Edit Manual Entry" : "Add Past Entry"}</DialogTitle>
            <DialogDescription>
              Manual entries share the same project and reporting pipeline as timer-created sessions.
            </DialogDescription>
          </DialogHeader>
          <FieldGroup className="grid gap-4 md:grid-cols-2">
            <Field className="md:col-span-2">
              <FieldLabel htmlFor="entry-project">Project</FieldLabel>
              <Select
                items={projects.map((project) => ({
                  value: project.id,
                  label: projectLabel(project.id),
                }))}
                onValueChange={(value) => {
                  if (value !== null) setProjectId(value);
                }}
                value={projectId}
              >
                <SelectTrigger className="w-full" id="entry-project">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {projects.map((project) => (
                    <SelectItem key={project.id} value={project.id}>
                      {projectLabel(project.id)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
            <DateTimePicker
              id="entry-start"
              label="Start time"
              value={startTime}
              onChange={setStartTime}
            />
            <DateTimePicker
              id="entry-end"
              label="End time"
              value={endTime}
              onChange={setEndTime}
            />
            <Field className="md:col-span-2">
              <FieldLabel htmlFor="entry-note">Session note</FieldLabel>
              <Textarea
                id="entry-note"
                onChange={(event: ChangeEvent<HTMLTextAreaElement>) => setNote(event.target.value)}
                placeholder="Optional note for the session"
                value={note}
              />
            </Field>
          </FieldGroup>
          {validationMessage ? <Banner message={validationMessage} tone="error" /> : null}
          <DialogFooter>
            <Button onClick={() => onOpenChange(false)} type="button" variant="ghost">
              Cancel
            </Button>
            <Button disabled={Boolean(validationMessage)} type="submit">
              Save Entry
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
